import { Box, Typography } from "@mui/material";
import { TTransferType } from "../../types";
import TransferRequestAction from "./TransferRequestAction";
import { useState } from "react";
import {
	ICreateTransferRequest,
	IInventoryTradeVehicles,
	IMappedTransferRequest,
	TSearchTransferRequest,
	TTransferInDefaultSearchTransferRequest,
} from "../../types/dealerTrades";
import { TransferRequestTable } from "../../Tables/DealerTrades/TransferRequestTable";
import { UseMutateFunction } from "@tanstack/react-query";
import { resolveDefaultTransferRequest } from "../../helpers";
import { TRANSFER_IN, TRANSFER_OUT } from "../../constants";

type TSwapRequestProps = {
	locationId: string;
	transferType: TTransferType;
	commissionCycleId?: string;
	payPeriodId: string;
	isCreatingTransferRequest?: boolean;
	locationsOptions: {
		isLoadingLocations: boolean;
		mappedLocations: { value: string; label: string }[];
		franchisesByLocations: { franchises: string[]; locationId: string }[];
	};
	uniqueVinNumbers: string[];
	isCurrentPayPeriod: boolean;
	setTransferType: (value: TTransferType) => void;
	mutate: UseMutateFunction<unknown, Error, IMappedTransferRequest[], unknown>;
};

const SwapRequest = (props: TSwapRequestProps) => {
	const {
		locationId,
		transferType,
		setTransferType,
		commissionCycleId = "",
		payPeriodId,
		locationsOptions,
		mutate,
		isCreatingTransferRequest,
		uniqueVinNumbers,
		isCurrentPayPeriod,
	} = props;
	const [swapVehicles, setSwapVehicles] = useState<ICreateTransferRequest[]>([]);
	const defaultLocation = swapVehicles[0]
		? [
				{ value: swapVehicles[0].originLocationId, group: "destination" },
				{ value: swapVehicles[0].destinationLocationId, group: "origin" },
		  ]
		: [];

	const transferOutDefaultSearchTransferRequest = resolveDefaultTransferRequest(uniqueVinNumbers, [
		{ value: locationId, group: "origin" },
	]);
	const transferInDefaultSearchTransferRequest: TTransferInDefaultSearchTransferRequest = resolveDefaultTransferRequest(
		uniqueVinNumbers,
		defaultLocation
	) ?? {
		location: defaultLocation,
	};

	if ("vinNumber" in transferInDefaultSearchTransferRequest) {
		delete transferInDefaultSearchTransferRequest.vinNumber;
	}

	const handleReceivingOnCreate = (filters: Partial<TSearchTransferRequest>, data?: IInventoryTradeVehicles) => {
		if (data && isCurrentPayPeriod) {
			const { locationId: originLocationId } = data;
			setSwapVehicles([
				{
					transferType,
					originLocationId,
					destinationLocationId: filters?.location?.find((loc) => loc.group === "destination")?.value ?? "",
					vehicle: data,
				},
			]);
		}
	};

	const handleGivingOnCreate = (filters: Partial<TSearchTransferRequest>, data?: IInventoryTradeVehicles, cleaner?: () => void) => {
		if (data && isCurrentPayPeriod) {
			const { locationId: originLocationId } = data;
			const mappedSwapVehicles = [
				...swapVehicles,
				{
					commissionCycleId,
					payPeriodId,
					transferType,
					originLocationId,
					destinationLocationId: filters?.location?.find((loc) => loc.group === "destination")?.value ?? "",
					vehicle: data,
				},
			].map((item) => {
				const { originLocationId: olId, destinationLocationId, vehicle } = item;
				const { stock, vin, make, model, trim, colorExterior, year, type, _id, transferAmount, status } = vehicle;
				return {
					transferType,
					destinationLocationId,
					originLocationId: olId,
					vehicle: {
						stockNumber: stock,
						vinNumber: vin,
						year: Number(year),
						_id,
						make,
						model,
						trim,
						colorExterior,
						type,
						transferAmount,
						status,
					},
				};
			});
			mutate(mappedSwapVehicles, {
				onSuccess: () => {
					cleaner?.();
				},
				onError: () => {
					cleaner?.();
				},
			});
		}
	};

	return (
		<Box className="flex flex-col pt-8">
			<Box className="flex items-center ">
				<Typography className="!text-lg !font-montserrat !font-medium !text-default">Giving</Typography>
			</Box>
			<Box className="!mt-[12px]">
				{!swapVehicles.length && (
					<TransferRequestAction
						{...props}
						table="createTransferRequest"
						transferType={TRANSFER_OUT}
						iconType="Asset Swap"
						setTransferType={setTransferType}
						onCreate={handleReceivingOnCreate}
						defaultSearchTransferRequest={transferOutDefaultSearchTransferRequest}
					/>
				)}
			</Box>
			{!!swapVehicles.length && (
				<>
					<TransferRequestTable
						locationId={locationId}
						table=""
						transferType={TRANSFER_OUT}
						commissionCycleId={commissionCycleId}
						payPeriodId={payPeriodId}
						iconType="Asset Swap"
						locationsOptions={locationsOptions}
						inventoryTradeVehicles={[swapVehicles[0].vehicle]}
						onCreateTransferRequest={() => {}}
						isLoadingInventory={false}
						hasNextPage={false}
						enableActions={false}
						disableTransferAmountDialog={true}
						isCurrentPayPeriod={isCurrentPayPeriod}
					/>
					<Box className="flex items-center my-[25px]">
						<Typography className="!text-lg !font-montserrat !font-medium !text-default">Receiving</Typography>
					</Box>
					<TransferRequestAction
						{...props}
						table="createTransferRequest"
						defaultSearchTransferRequest={transferInDefaultSearchTransferRequest}
						disableLocationOptionsAndValidation={true}
						transferType={TRANSFER_IN}
						iconType="Asset Swap"
						setTransferType={setTransferType}
						onCreate={handleGivingOnCreate}
						isCreatingTransferRequest={isCreatingTransferRequest}
						isCurrentPayPeriod={isCurrentPayPeriod}
					/>
				</>
			)}
		</Box>
	);
};

export default SwapRequest;
